import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Home, 
  ArrowLeft, 
  MapPin, 
  Gem, 
  Search,
  Mountain,
  AlertTriangle
} from 'lucide-react';
import SEOHead from '../components/SEOHead';

const NotFoundPage = () => {
  // Popular pages to suggest
  const popularPages = [
    {
      title: "Underground Tours",
      description: "Experience authentic opal mine tours",
      href: "/underground-tours",
      icon: Mountain,
      badge: "Popular"
    },
    {
      title: "Opal Store", 
      description: "Shop authentic Australian opals",
      href: "/opal-store",
      icon: Gem,
      badge: "Shop"
    },
    {
      title: "Book Tour Tickets",
      description: "Reserve your underground experience",
      href: "/book-tour", 
      icon: MapPin,
      badge: "Book Now"
    },
    {
      title: "Free Museum",
      description: "Visit our mining history museum",
      href: "/free-museum",
      icon: Search,
      badge: "Free"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <SEOHead
        title="Page Not Found - 404 Error"
        description="The page you're looking for doesn't exist. Explore Umoona Opal Mine's underground tours, authentic opal store, and free museum instead."
        canonical="/404"
        noIndex={true}
      />
      
      <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          
          {/* 404 Hero Section */}
          <div className="mb-12">
            {/* Large 404 with mining theme */}
            <div className="relative mb-8">
              <div className="text-8xl md:text-9xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-yellow-300 to-orange-500 leading-none">
                404
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <AlertTriangle className="w-12 h-12 md:w-16 md:h-16 text-orange-500 opacity-30" />
              </div>
            </div>

            {/* Mining-themed error message */}
            <div className="space-y-4 mb-8">
              <Badge className="bg-orange-100 text-orange-800 border-orange-200 px-4 py-2">
                ⚠️ Lost in the Underground?
              </Badge>
              
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                This Page Has Gone Underground!
              </h1>
              
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Looks like this page has been buried deeper than our opals! 
                Don't worry, there's plenty more to explore above ground.
              </p>
            </div>

            {/* Quick action buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button asChild className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3">
                <Link to="/">
                  <Home className="w-5 h-5 mr-2" />
                  Back to Surface (Home)
                </Link>
              </Button>
              
              <Button asChild variant="outline" className="border-purple-300 text-purple-600 hover:bg-purple-50 px-8 py-3">
                <Link to="/contact">
                  <MapPin className="w-5 h-5 mr-2" />
                  Get Directions
                </Link>
              </Button>
            </div>
          </div>

          {/* Popular Destinations */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Explore These Popular Destinations Instead
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {popularPages.map((page, index) => {
                const IconComponent = page.icon;
                return (
                  <Card key={index} className="hover:shadow-lg transition-all duration-300 hover:scale-105 group">
                    <CardContent className="p-6">
                      <Link to={page.href} className="block">
                        <div className="flex items-start space-x-4">
                          <div className="bg-purple-100 p-3 rounded-lg group-hover:bg-purple-200 transition-colors">
                            <IconComponent className="w-6 h-6 text-purple-600" />
                          </div>
                          
                          <div className="flex-1 text-left">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-semibold text-gray-900 group-hover:text-purple-600 transition-colors">
                                {page.title}
                              </h3>
                              <Badge className="bg-orange-100 text-orange-700 text-xs">
                                {page.badge}
                              </Badge>
                            </div>
                            <p className="text-gray-600 text-sm">
                              {page.description}
                            </p>
                          </div>
                          
                          <ArrowLeft className="w-5 h-5 text-purple-400 transform rotate-180 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </Link>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Contact Information */}
          <Card className="bg-gradient-to-r from-slate-800 to-slate-900 text-white">
            <CardContent className="p-8 text-center">
              <h3 className="text-xl font-bold mb-4">Still Need Help Finding Your Way?</h3>
              <p className="text-slate-300 mb-6">
                Our friendly staff at Umoona Opal Mine are here to help guide you to the right place.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <div className="flex items-center text-slate-300">
                  <MapPin className="w-4 h-4 mr-2" />
                  <span>Hutchison Street, Coober Pedy SA 5723</span>
                </div>
                
                <div className="hidden sm:block text-slate-500">•</div>
                
                <div className="flex items-center text-slate-300">
                  <span className="mr-2">📞</span>
                  <span>(08) 8672 5288</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Fun mining fact */}
          <div className="mt-12 p-6 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl border border-orange-200">
            <div className="flex items-center justify-center mb-3">
              <Gem className="w-8 h-8 text-orange-500 mr-3" />
              <h3 className="text-lg font-semibold text-orange-800">Did You Know?</h3>
            </div>
            <p className="text-orange-700">
              Coober Pedy produces about 70% of the world's opals! While you're here, 
              why not explore our underground mine or browse our authentic opal collection?
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotFoundPage;